import React, { useState, useEffect } from "react";
import axios from 'axios';

import './SettingsPage.css';
import editIcon from '../../assets/images/edit-icon.svg';
import ChangePasswordModal from "../../components/ChangePasswordModal";
import ChangeEmailModal from "../../components/ChangeEmailModal";
import ConfirmationModal from '../../components/ConfirmationModal';

import { API_BASE_URL, DEFAULT_PROFILE_PICTURE } from "../../Variables";
import { useNavigate } from "react-router-dom";

function SettingsPage () {
  const navigate = useNavigate();
  const [userSettings, setUserSettings] = useState({});
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [refreshToggle, setRefreshToggle] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  const token = localStorage.getItem('token');

  // Fetch user settings on mount
  useEffect(() => {

    const fetchUserSettings = async () => {
      try {
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUserSettings(response.data);
      } catch (err) {
        setError('Failed to fetch settings');
      }
    };

    fetchUserSettings();
  }, [token, refreshToggle]);

  // Fetch profile picture
  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/profile-pictures`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          responseType: "blob",
        });
    
        const imageUrl = URL.createObjectURL(response.data);
        setImagePreview(imageUrl);
      } catch (error) {
        setError("Failed to load profile picture");
      }
    };

    if (userSettings.profile_picture) {
      fetchProfilePicture();
    }
  }, [userSettings.profile_picture]);

  // Refresh settings
  const refresh = () => {
    setRefreshToggle(!refreshToggle);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserSettings(prevSettings => ({
      ...prevSettings,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Save settings
  const handleSave = async () => {
    try {
      const formData = new FormData();
      
      for (const [key, value] of Object.entries(userSettings)) {
        formData.append(key, value);
      }

      if (selectedImage) {
        formData.append('profile_picture', selectedImage);
      }

      let response = await axios.put(`${API_BASE_URL}/settings`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
  
      setError('');
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 4000);

      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (err) {
      if (err.response && err.response.data.error) {
        setError(err.response.data.error);
      } else{
        setError('Failed to save settings');
      }
    }
  };
  
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();

      reader.onloadend = () => {
        setImagePreview(reader.result);
      };

      reader.readAsDataURL(file);
    }
  };

    // Delete user
    const handleDeleteUser = async (userId) => {
      try {
        await axios.delete(`${API_BASE_URL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });

        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('user_id');
        navigate('/login');
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          setError(err.response.data.error);
        } else {
          setError('Failed to delete user');
        }
      } finally {
        setShowConfirmDelete(false);
      }
    };

  return (
    <div className="settings-page">

      <button onClick={() => setShowEmailModal(true)} className="secondary-button">Change Email</button>
      <button onClick={() => setShowPasswordModal(true)} className="secondary-button">Change Password</button>
      <button onClick={() => setShowConfirmDelete(true)} className="important-button">Delete Account</button>
  
      <div className="settings-form">

        <div className="form-input">
          <label>Email: {userSettings.email}</label>
        </div>

        <div className="image-upload-container">
          <input
            type="file"
            id="profile-picture"
            onChange={handleImageChange}
            hidden
          />
          <label htmlFor="profile-picture" className="image-label"> 
            <div className="signup-profile-picture" >
              <img src={imagePreview ? imagePreview : `${API_BASE_URL}${DEFAULT_PROFILE_PICTURE}`} alt="Profile" className="profile-picture"/>
              <div className="edit-overlay"><img src={editIcon} className="edit-icon" alt="Edit pen" /></div>
            </div>
          </label>
        </div>

        <div className="form-input">
        <label>Display Name: </label>
          <input type="text" name="display_name" value={userSettings.display_name || ''} onChange={handleChange}/>
        </div>

        <div className="form-input">
        <label>Bio:</label>
          <textarea name="bio" value={userSettings.bio || ''} onChange={handleChange} placeholder="optional" maxLength={150} rows="5"/>
        </div>

        <div className="checkboxes-container">
          <div className="checkbox-container">
            <label>Announcements:</label>
            <input type="checkbox" name="announcements" checked={userSettings.announcements || false}
              onChange={handleChange} className="checkbox"/>
          </div>

          <div className="checkbox-container">
          <label>Recommendations:</label>
            <input type="checkbox" name="recommendations"
              checked={userSettings.recommendations || false} onChange={handleChange}/>
          </div>

          <div className="checkbox-container">
            <label>Reminders:</label>
              <input type="checkbox" name="reminders"
                checked={userSettings.reminders || false} onChange={handleChange}/>
          </div>

          <div className="checkbox-container">
          <label>Updates:</label>
            <input type="checkbox" name="updates"
              checked={userSettings.updates || false} onChange={handleChange}/>
          </div>

          <div className="checkbox-container">
            <label> Track Interactions:</label>
            <input type="checkbox" name="track_interactions"
              checked={userSettings.track_interactions || false} onChange={handleChange}/>
          </div>
        </div>
        
        <div className="form-input">
          <label>Profile Visibility:</label>
          <select name="profile_visibility" value={userSettings.profile_visibility || 'public'}
            onChange={handleChange} className="form-select">
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
        </div>

        <div className="form-input">
          <label>Main Shelves Visibility:</label>
          <select name="mandatory_shelves_visibility" value={userSettings.mandatory_shelves_visibility || 'public'}
            onChange={handleChange} className="form-select">
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
        </div>

        <div className="form-input">
          <label>Custom Shelves Visibility:</label>
          <select name="custom_shelves_visibility" value={userSettings.custom_shelves_visibility || 'public'}
            onChange={handleChange} className="form-select">
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
        </div>

        <div className="form-input">
          <label>Reviews Visibility:</label>
          <select name="reviews_visibility" value={userSettings.reviews_visibility || 'public'}
            onChange={handleChange} className="form-select">
            <option value="public">Public</option>
            <option value="private">Private</option>
          </select>
        </div>

        <button className="primary-button" onClick={handleSave}>Save Settings</button>
      </div>

      {error && <p className="error">{error}</p>}
      {successMessage && <p className="success">{successMessage}</p>}

      {showEmailModal && (
        <ChangeEmailModal onClose={() => setShowEmailModal(false)} refresh={refresh}/>
      )}

      {showPasswordModal && (
        <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
      )}

      {showConfirmDelete && (
        <ConfirmationModal
          onClose={() => setShowConfirmDelete(false)}
          onConfirm={() => handleDeleteUser(userSettings.id)}
          title={`Are you sure you want to delete the your account"?`}
          message={
            "This action cannot be undone. All your data will be lost."
          }
        />
      )}
    </div>
  );
}

export default SettingsPage;
